import type { UIElement } from '@adyen/adyen-web';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { createAdyenComponent } from '@/utils/adyen';
import { isCheckoutAddressesMissing } from '@/utils/checkout';

import { APOLLO_CLIENTS } from '@/graphql/client';
import {
  useOrderAbandonMutation,
  useOrderCreateMutation,
} from '@/graphql/orderApp/api';
import type { CountryCode } from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGetPaymentGateways } from '@/hooks/useGetPaymentGateways';
import { useRouter } from '@/hooks/useRouter';
import { useTransactionInitialize } from '@/hooks/useTransactionInitialize';
import { useUser } from '@/hooks/useUser';
import { updateAdyenPayment } from '@/lib/adyen';
import { getAmountInMinorUnits } from '@/lib/amount';
import {
  EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION,
  MOCK_DEFAULT_EMAIL,
  MOCK_DEFAULT_VALUE,
  PAYMENT_APPS_ID,
} from '@/lib/const';
import { captureError, handlePromiseErrors } from '@/lib/errors';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { fetchOrderData } from '@/lib/order';
import {
  createAdyenCheckout,
  handleTransactionResponse,
  PAYMENT_QUERY_PARAMS,
} from '@/lib/payment/helpers';
import {
  type AdyenCheckoutOpts,
  PAYMENT_TYPES,
  type TransactionAction,
  type TransactionType,
} from '@/lib/payment/types';
import { formatPhoneWithCallingCode } from '@/lib/phone';
import { handlePhonePrefix } from '@/lib/phonePrefixes/helpers';
import { StorageHelper } from '@/lib/storage';
import type { ApiError, ApiErrorType } from '@/lib/types';
import { useProcessingContext } from '@/providers/ProcessingProvider/ProcessingProviderContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';

import { Loader } from '../Loader';
import { getNamesFromShippingAddress } from './helpers';

interface PaypalPayer {
  address: {
    country_code: string;
  };
  email_address: string;
  name:
    | {
        given_name: string;
        surname: string;
      }
    | undefined;
  phone:
    | {
        phone_number:
          | {
              national_number: string;
            }
          | undefined;
        phone_type: string;
      }
    | undefined;
}
interface PaypalExpressButtonProps {
  areBraceletSizeErrorsSet: () => boolean;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
  zIndex?: number;
}

export const PaypalExpressButton: FC<PaypalExpressButtonProps> = ({
  areBraceletSizeErrorsSet,
  errorHandler,
  zIndex,
}) => {
  const { paths, push } = useRouter();
  const { formatMessage } = useIntl();
  const paypalButtonRef = useRef<HTMLDivElement>(null);
  const transactionRef = useRef<string | undefined>(undefined);
  const {
    country,
    locale,
    query: { languageCode },
  } = useRegionProvider();
  const { setIsProcessing } = useProcessingContext();
  const {
    addressUpdate,
    emailUpdate,
    endProcessing,
    refresh,
    snapshot: {
      checkout: { id, totalPrice },
      checkout,
      errors,
      isChangedAfterOrderCreated,
      orderId,
      paymentMethod,
      preOrderData,
    },
    startProcessing,
  } = useCheckout<true>();
  const {
    snapshot: { user },
  } = useUser();
  const isDepositTransaction = !!preOrderData?.deposit;
  // FF to limite paypal express preorder on certain countries
  const isExpressPaymentAllowed = !isDepositTransaction
    ? true
    : EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION.length === 0 ||
      EXPRESS_PAYMENT_PREORDER_COUTRY_LIMITATION.includes(country.code);

  const storageHelper = new StorageHelper();
  const [isTransactionSuccessfully, setIsTransactionSuccessfully] =
    useState<boolean>(false);

  const useNetAmount = ['US', 'CA'].includes(country.code);
  const totalPriceFinal = useMemo(
    () => (useNetAmount ? totalPrice.net : totalPrice.gross),
    [useNetAmount, totalPrice],
  );
  const depositAmount = useMemo(() => {
    if (!preOrderData) return 0;
    return useNetAmount ? preOrderData.deposit_net : preOrderData.deposit;
  }, [useNetAmount, preOrderData]);

  const transactionAmount = useMemo(
    () => (isDepositTransaction ? depositAmount : totalPriceFinal.amount),
    [isDepositTransaction, depositAmount, totalPriceFinal.amount],
  );

  const errorHandlerPaypalExpress =
    errorHandler ??
    genericErrorHandler({
      formatMessage,
      apiErrorsHandler: checkoutActions.setErrors,
      errorType: 'adyen',
    });

  const braceletSizeCheckRef = useRef<() => boolean>(() => true);
  useEffect(() => {
    braceletSizeCheckRef.current = areBraceletSizeErrorsSet;
  }, [areBraceletSizeErrorsSet]);

  const setError = () => {
    errorHandlerPaypalExpress(
      [
        {
          code: 'PAYMENT_ERROR',
        },
      ],
      { errorType: 'adyen' },
    );
    setIsProcessing(false);
    setIsTransactionSuccessfully(false);
    return false;
  };

  const handleShopperDetails = async (data: any, actions: any) => {
    const userPhone =
      getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phone) || '';
    const userPhonePrefix = handlePhonePrefix(
      getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phonePrefix) || '',
    );
    const userPhoneFull = `${userPhonePrefix}${userPhone}`;
    const payer: PaypalPayer | undefined = data.authorizedEvent?.payer;
    const shippingAddress = data.deliveryAddress;
    const billingAddress = data.billingAddress;

    const payerPhoneNumber = formatPhoneWithCallingCode(
      payer?.phone?.phone_number?.national_number,
      payer?.address?.country_code,
    );

    // Paypal return the full name in the firstName field...
    const { firstName, lastName } =
      getNamesFromShippingAddress(shippingAddress);

    // Convert PayPal addresses to the expected format
    const shippingAddressInput = {
      firstName: firstName || '',
      lastName: lastName || '',
      companyName: '',
      streetAddress1: shippingAddress?.street || '',
      streetAddress2: '',
      city: shippingAddress?.city || '',
      cityArea: '',
      postalCode: shippingAddress?.postalCode || '',
      phone: userPhoneFull || payerPhoneNumber || '',
      country: (shippingAddress?.country as CountryCode) || '',
      countryArea: shippingAddress?.stateOrProvince || '',
      metadata: [
        {
          key: ACCOUNT_METADATA.phone,
          value: payerPhoneNumber,
        },
      ],
      skipValidation: true,
    };

    // If billing address is not provided, use shipping address from PayPal
    const billingAddressInput = {
      firstName: payer?.name?.given_name || billingAddress?.firstName || '',
      lastName: payer?.name?.surname || billingAddress?.lastname || '',
      companyName: '',
      streetAddress1: billingAddress?.street || shippingAddress?.street || '',
      streetAddress2: '',
      city: billingAddress?.city || shippingAddress?.city || '',
      cityArea: '',
      postalCode:
        billingAddress?.postalCode || shippingAddress?.postalCode || '',
      phone: userPhoneFull || payerPhoneNumber || '',
      country:
        (billingAddress?.country as CountryCode) ||
        (shippingAddress?.country as CountryCode) ||
        '',
      countryArea:
        billingAddress?.stateOrProvince ||
        shippingAddress?.stateOrProvince ||
        '',
      metadata: [
        {
          key: ACCOUNT_METADATA.phone,
          value: payerPhoneNumber,
        },
      ],
    };

    const updateAddressWithRetry = async (
      shipping: typeof shippingAddressInput,
      billing: typeof billingAddressInput,
      shopperEmail: string | undefined,
      retry = false,
    ): Promise<void> => {
      if (!orderRef.current) return;

      const tasks: Promise<unknown>[] = [
        addressUpdate('shipping', shipping),
        addressUpdate('billing', billing),
      ];

      if (shopperEmail) {
        tasks.push(emailUpdate(shopperEmail));
      }

      const results = await Promise.allSettled(tasks);
      const errors = handlePromiseErrors(results);
      const checkoutPhoneError = errors.some(
        (error: any) => error.field === 'phone' && error.code === 'INVALID',
      );

      const response = await fetch(paths.api.express.updateOrder.asPath(), {
        method: 'POST',
        body: JSON.stringify({
          id: orderRef.current,
          shippingAddress: shipping,
          billingAddress: billing,
          userEmail: user?.email || shopperEmail || '',
        }),
      });

      if (response.ok) return;

      const body = await response.json();
      const phoneError = body.errors?.some(
        (error: any) => error.field === 'phone' && error.code === 'INVALID',
      );

      if ((phoneError || checkoutPhoneError) && !retry) {
        // Retry once without the problematic PayPal phone number
        shipping.phone = '';
        billing.phone = '';
        return updateAddressWithRetry(shipping, billing, shopperEmail, true);
      }

      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    };

    const runUpdateAddress = async () => {
      try {
        const tasks: Promise<unknown>[] = [
          updateAddressWithRetry(
            shippingAddressInput,
            billingAddressInput,
            payer?.email_address,
          ),
        ];

        if (user?.email) {
          tasks.push(emailUpdate(user.email));
        } else if (payer?.email_address) {
          tasks.push(emailUpdate(payer.email_address));
        }

        const results = await Promise.allSettled(tasks);
        const errors = handlePromiseErrors(results);
        if (errors.length) {
          console.error('PayPal Express errors:', errors);
          return setError();
        }

        actions.resolve();
      } catch (error) {
        console.error('PayPal Express errors:', error);
        setError();
      }
    };

    runUpdateAddress();
  };

  const handleError: AdyenCheckoutOpts['onError'] = async (error) => {
    setIsProcessing(false);

    const isCancelError = (err: unknown): boolean => {
      if (!err) return false;

      const cancelKeywords = ['CANCEL', 'Window is closed', 'popup close'];

      const containsCancelKeyword = (text: string): boolean => {
        return cancelKeywords.some((keyword) => text.includes(keyword));
      };

      if (typeof err === 'string') {
        return containsCancelKeyword(err);
      }

      if (err && typeof err === 'object') {
        if (typeof (err as any).toString === 'function') {
          const errorString = (err as any).toString();
          if (containsCancelKeyword(errorString)) {
            return true;
          }
        }

        const errorString = JSON.stringify(err);
        if (containsCancelKeyword(errorString)) {
          return true;
        }

        if ('message' in err && typeof (err as any).message === 'string') {
          return containsCancelKeyword((err as any).message);
        }
      }

      return false;
    };

    if (!isCancelError(error)) {
      captureError(error, {
        extra: {
          error,
          checkoutId: id,
        },
      });
      errorHandlerPaypalExpress([{ code: 'PAYMENT_ERROR' }]);
    }

    // We abandon the current order to prevent poluting the backoffice with unuseable orders
    if (orderRef.current) {
      const { data } = await fetchOrderData(orderRef.current, languageCode);

      const shouldAbandonOrder =
        data?.order?.userEmail === 'no-reply@breitling.com' &&
        (data?.order?.shippingAddress?.firstName === 'MOCK' ||
          data?.order?.shippingAddress?.lastName === 'MOCK') &&
        data?.order?.shippingAddress?.phone === '' &&
        (data?.order?.billingAddress?.firstName === 'MOCK' ||
          data?.order?.billingAddress?.lastName === 'MOCK') &&
        data?.order?.billingAddress?.phone === '';

      if (shouldAbandonOrder) {
        const res = await orderAbandon({
          variables: { checkoutId: id, orderId: orderRef.current ?? '' },
        });

        if (res.errors?.length) {
          errorHandlerPaypalExpress(errors, { errorType: 'orderApp' });
          return endProcessing();
        }
        // Needed to avoid using the abandoned order on next paypal express popup opening
        orderRef.current = undefined;
      }
    }
  };

  const { gatewayConfigs, isLoading: isGatewayConfigLoading } =
    useGetPaymentGateways(
      orderId || id, // Use checkout ID if orderId is empty
      totalPriceFinal?.amount,
    );

  const transactionInitialize = useTransactionInitialize(id);
  const orderRef = useRef<string | undefined>(orderId || '');
  const isChangedAfterOrderCreatedRef = useRef<boolean>(
    isChangedAfterOrderCreated,
  );
  const gatewayId = useMemo(() => {
    const paypalGateway = gatewayConfigs.find((g) =>
      g.paymentMethods.some((m) => m.type === PAYMENT_TYPES.PAYPAL_EXPRESS),
    );

    return paypalGateway?.id;
  }, [gatewayConfigs]);

  const [isPaypalExpressActivated, setIsPaypalExpressActivated] =
    useState<boolean>(false);

  useEffect(() => {
    const isPaypalExpressActivated = PAYMENT_APPS_ID.some((appId) => {
      const gateway = gatewayConfigs.find((item) => item.id === appId);
      return gateway?.paymentMethods.some(
        (method) => method.type === PAYMENT_TYPES.PAYPAL_EXPRESS,
      );
    });

    if (isPaypalExpressActivated) {
      setIsPaypalExpressActivated(true);
      setIsTransactionSuccessfully(false);
    }
  }, [gatewayConfigs]);

  const handleSubmit =
    (gatewayId?: string): AdyenCheckoutOpts['onSubmit'] =>
    async (state, el) => {
      const extraInfo = {
        paymentMethod: {
          type: paymentMethod?.type,
          storedPaymentMethodId: paymentMethod?.extraInfo?.id,
        },
      };
      const handleAction: UIElement['handleAction'] = (...props) => {
        return el.handleAction(...props);
      };
      startProcessing();
      el.setStatus('loading');
      try {
        const type = isDepositTransaction ? 'DEPOSIT' : 'FULL';

        const success = await handlePayment({
          action: 'AUTHORIZE' as unknown as TransactionAction,
          type,
          detail: state.data,
          gatewayId,
          extraInfo,
          handleAction,
        });
        if (!success) {
          if (typeof (el as any).handleReject === 'function') {
            (el as any).handleReject('CANCEL', 'Payment processing failed');
          }
          endProcessing();
          return false;
        }
      } catch (err) {
        if (typeof (el as any).handleReject === 'function') {
          (el as any).handleReject(
            'CANCEL',
            'An error occurred during payment processing',
          );
        }
        throw err;
      } finally {
        el.setStatus('ready');
        endProcessing();
      }
    };

  const createOrder = async (checkoutId: string) => {
    checkoutActions.setOrderId('');
    const { data } = await orderCreate({
      variables: { id: checkoutId },
    });

    const errors = data?.orderCreate?.errors;
    if (errors?.length) {
      errorHandlerPaypalExpress(errors, { errorType: 'orderApp' });
      return false;
    }
    orderRef.current = data?.orderCreate?.order?.id;
    orderRef.current && checkoutActions.setOrderId(orderRef.current);
    isChangedAfterOrderCreatedRef.current = false;
    checkoutActions.setIsChangedAfterOrderCreated(false);

    return !!orderRef.current;
  };

  const [orderCreate] = useOrderCreateMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const [orderAbandon] = useOrderAbandonMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const handlePayment = async ({
    action,
    detail,
    extraInfo,
    gatewayId,
    handleAction,
    type,
  }: {
    action: TransactionAction;
    detail: object;
    extraInfo: object;
    gatewayId?: string;
    handleAction?: UIElement['handleAction'];
    type: TransactionType;
  }) => {
    if (
      !orderRef.current ||
      (orderRef.current && isChangedAfterOrderCreatedRef.current)
    ) {
      if (isChangedAfterOrderCreatedRef.current && orderRef.current) {
        const res = await orderAbandon({
          variables: { checkoutId: id, orderId: orderRef.current ?? '' },
        });
        if (res.errors?.length) {
          errorHandlerPaypalExpress(errors, { errorType: 'orderApp' });
          return endProcessing();
        }
      }

      const success = await createOrder(checkout.id);
      if (!success) return false;
    }

    if (!orderRef.current) return false;

    const { data } = await transactionInitialize({
      id: orderRef.current,
      action,
      amount: transactionAmount,
      data: {
        ...detail, // ? to confirm
        extraInfo,
      },
      gatewayId,
      type,
    });

    transactionRef.current = data?.transactionInitialize?.transaction?.id;

    const _params = {
      handleAction,
      errorHandler: errorHandlerPaypalExpress,
      paymentType: PAYMENT_TYPES.PAYPAL,
      setIsTransactionSuccessfully,
    };
    await handleTransactionResponse(data?.transactionInitialize?.data, _params);
  };

  const handleAdditionalDetails: AdyenCheckoutOpts['onAdditionalDetails'] =
    async (state: Record<string, any>, el: UIElement) => {
      setIsProcessing(true);
      el.setStatus('loading');

      const stateData = state.data;
      if (transactionRef.current) {
        const result = await updateAdyenPayment(
          transactionRef.current,
          stateData.paymentData,
        );
        if (result.payment_data) {
          stateData.paymentData = result.payment_data;
        } else {
          return setError();
        }
      }
      if (orderRef.current) {
        storageHelper.setSessionStorage('breitling.pe_state_data', stateData);
        storageHelper.setSessionStorage(
          'breitling.pe_transaction_ref',
          transactionRef.current,
        );
        const reviewExpressUrl = paths.store.checkout.reviewExpress.url({
          query: {
            [PAYMENT_QUERY_PARAMS.orderId]: orderRef.current,
          },
        });

        push(reviewExpressUrl);
      } else {
        return setError();
      }
    };

  const handleShippingAddressChange = async (data: any, actions: any) => {
    setIsProcessing(true);
    if (data.shippingAddress.countryCode !== country?.code) {
      return actions.reject();
    }

    //We need to remove the id from the shipping address
    const shippingAddress = {
      firstName: checkout.shippingAddress?.firstName || MOCK_DEFAULT_VALUE,
      lastName: checkout.shippingAddress?.lastName || MOCK_DEFAULT_VALUE,
      companyName: checkout.shippingAddress?.companyName || '',
      streetAddress1:
        checkout.shippingAddress?.streetAddress1 || MOCK_DEFAULT_VALUE,
      streetAddress2: checkout.shippingAddress?.streetAddress2 || '',
      cityArea: checkout.shippingAddress?.cityArea || '',
      phone: checkout.shippingAddress?.phone || '',
      metadata: [],
      city: data.shippingAddress.city,
      country: data.shippingAddress.countryCode,
      postalCode: data.shippingAddress.postalCode,
      countryArea: data.shippingAddress.state,
      skipValidation: true,
    };

    addressUpdate('shipping', shippingAddress);
    if (orderRef.current) {
      fetch(paths.api.express.updateOrder.asPath(), {
        method: 'POST',
        body: JSON.stringify({
          id: orderRef.current,
          shippingAddress: shippingAddress,
        }),
      });
    }
  };

  const initialise = async () => {
    if (!paypalButtonRef.current) return;

    const adyenCheckout = await createAdyenCheckout({
      showPayButton: true,
      countryCode: country.code,
      price: {
        ...totalPriceFinal,
        amount: getAmountInMinorUnits(
          transactionAmount,
          totalPriceFinal.currency,
        ),
      },
      onSubmit: handleSubmit(gatewayId),
      onError: handleError,
      locale: locale.locale,
      formatMessage,
      paymentMethodType: PAYMENT_TYPES.PAYPAL,
      onAdditionalDetails: handleAdditionalDetails,
    });

    const paypalDropinConfiguration = gatewayConfigs
      .find((item) => item.id === gatewayId)
      ?.paymentMethods.find(
        (item) => item.type === PAYMENT_TYPES.PAYPAL,
      )?.dropInConfiguration;

    const paypalComponent = createAdyenComponent(
      PAYMENT_TYPES.PAYPAL,
      adyenCheckout,
      {
        isExpress: true,
        userAction: 'continue',
        blockPayPalVenmoButton: true,
        blockPayPalCreditButton: true,
        blockPayPalPayLaterButton: true,
        onClick: handlePayPalClick,
        onShippingAddressChange: handleShippingAddressChange,
        onAuthorized: handleShopperDetails,
        onError: handleError,
        onCancel: handleError,
        intent: 'authorize',
        ...paypalDropinConfiguration,
      },
    );

    if (paypalButtonRef.current) {
      paypalComponent.mount(paypalButtonRef.current);
    }
  };

  useEffect(() => {
    initialise();
  }, [isPaypalExpressActivated, gatewayId, paypalButtonRef.current]);

  const handlePayPalClick = async () => {
    if (!braceletSizeCheckRef.current()) {
      return false;
    }

    const { billingAddressToUpdate, shippingAddressToUpdate } =
      isCheckoutAddressesMissing(checkout);

    if (billingAddressToUpdate || shippingAddressToUpdate) {
      const shippingRes = await fetch(
        paths.api.express.initCheckoutAddress.asPath(),
        {
          method: 'POST',
          body: JSON.stringify({
            id,
            code: country.code,
            shipping: shippingAddressToUpdate,
            billing: billingAddressToUpdate,
          }),
        },
      );
      if (!shippingRes.ok) {
        console.error('init checkout address for express payment fail');
        return;
      }
    }

    if (!checkout.email) {
      const emailRes = await emailUpdate(MOCK_DEFAULT_EMAIL);
      if (emailRes) {
        console.error('init checkout email for express payment fail');
        return;
      }
    }

    await refresh(true);
  };

  if (!isPaypalExpressActivated || !isExpressPaymentAllowed) {
    return null;
  }
  if (isTransactionSuccessfully || isGatewayConfigLoading) {
    return <Loader height={50} />;
  }

  return (
    <div
      ref={paypalButtonRef}
      data-testid='paypalExpressButton'
      style={{ zIndex, position: 'relative' }}
    />
  );
};
